/**
 * RoleSwitcher Component
 * 
 * A dropdown component that allows users with multiple roles (admin + employee)
 * to switch between different portal views without logging out.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Monitor, User, ChevronDown, Check, ArrowRightLeft } from 'lucide-react';

// All paths that belong to the employee-portal experience.
// Bug #25: Without this list, paths like /my-leave, /profile/settings, etc.
// fell through to the 'admin' default — showing "Admin Dashboard" as the
// active role while the user was clearly inside the employee portal layout.
const EMPLOYEE_PORTAL_PATHS = [
  '/employee-portal',
  '/my-leave',
  '/my-payslips',
  '/my-reimbursements',
  '/my-documents',
  '/my-training',
  '/notifications',
  '/profile',
];

const RoleSwitcher = ({ variant = 'default', showLabel = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, getAvailableRoles, switchRole, hasMultipleRoles, activeRole } = useAuth();
  const { isDark } = useTheme();

  // Don't render if user doesn't have multiple roles
  if (!hasMultipleRoles || !hasMultipleRoles()) {
    return null;
  }

  const availableRoles = getAvailableRoles ? getAvailableRoles() : [];

  // Determine current active role based on current path.
  // Employee portal pages use flat paths (/my-payslips, /my-leave, etc.)
  // that do NOT start with /employee-portal — we must list them explicitly.
  const EMPLOYEE_PORTAL_PATHS = [
    '/employee-portal',
    '/my-leave',
    '/my-payslips',
    '/my-loans',
    '/my-reimbursements',
    '/my-documents',
    '/my-swipe-requests',
    '/my-od-requests',
    '/profile',
    '/notifications',
  ];

  const getCurrentRole = () => {
    const path = location.pathname;
    if (EMPLOYEE_PORTAL_PATHS.some(p => path === p || path.startsWith(p + '/'))) {
      return 'employee';
    }
    if (
      path.startsWith('/dashboard') ||
      path.startsWith('/admin') ||
      path.startsWith('/settings') ||
      path.startsWith('/super-admin')
    ) {
      return 'admin';
    }
    // Fall back to the role that was explicitly selected via the switcher
    return activeRole || 'admin';
  };

  const currentRole = getCurrentRole();
  const currentRoleData = availableRoles.find(r => r.id === currentRole) || availableRoles[0];

  const handleRoleSwitch = (role) => {
    if (role.id !== currentRole) {
      if (switchRole) switchRole(role.id);
      navigate(role.path);
    }
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case 'Monitor':
        return <Monitor className="w-4 h-4" />;
      case 'User':
        return <User className="w-4 h-4" />;
      default:
        return <User className="w-4 h-4" />;
    }
  };

  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={`relative ${isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
            aria-label="Switch role"
          >
            <ArrowRightLeft className="w-5 h-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Switch View</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {availableRoles.map((role) => (
            <DropdownMenuItem
              key={role.id}
              onClick={() => handleRoleSwitch(role)}
              className="flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center gap-2">
                {getIcon(role.icon)}
                <span>{role.label}</span>
              </div>
              {role.id === currentRole && (
                <Check className="w-4 h-4 text-green-700" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={`flex items-center gap-2 ${
            isDark
              ? 'border-purple-500 text-purple-400 hover:bg-purple-900/20'
              : 'border-purple-500 text-purple-600 hover:bg-purple-50'
          }`}
          aria-label="Switch role"
        >
          {getIcon(currentRoleData?.icon)}
          {showLabel && (
            <span className="hidden sm:inline">{currentRoleData?.label}</span>
          )}
          <ChevronDown className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4" />
          Switch View
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableRoles.map((role) => (
          <DropdownMenuItem
            key={role.id}
            onClick={() => handleRoleSwitch(role)}
            className={`flex items-center justify-between cursor-pointer py-3 ${
              role.id === currentRole ? 'bg-purple-50 dark:bg-purple-900/20' : ''
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${
                role.id === currentRole 
                  ? 'bg-purple-100 dark:bg-purple-800' 
                  : 'bg-gray-100 dark:bg-slate-700'
              }`}>
                {getIcon(role.icon)}
              </div>
              <div>
                <p className="font-medium">{role.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-300">
                  {role.description}
                </p>
              </div>
            </div>
            {role.id === currentRole && (
              <Check className="w-5 h-5 text-green-700" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RoleSwitcher;

